import type { WalkthroughEvent } from '@shared-types/frontend-scenario-definition';
import { ref, watch } from 'vue';
import { isNullable } from '@/utils/nullability-helpers';
import { walkthroughEvents } from '@/config/walkthroughs';
import { createInjectionState } from '@vueuse/core';
import type { EventRowInsert } from '@shared-types/events.types';
import { useAuthStore } from '@/stores/auth';
import { addEvent } from '@/services/events.service';
import { useEventsStore } from '@/stores/events.store';

const [provideWalkthrough, useWalkthroughNullable] = createInjectionState(() => {
    const { completedEvents, cancelledEvents, events } = useEventsStore();
    const { currentOrganization, user } = useAuthStore();

    const walkthroughId = ref<string | undefined>(undefined);
    const walkthroughEvent = ref<WalkthroughEvent | undefined>(undefined);

    const walkthroughFinishLatestStatus = ref<'completed' | 'cancelled' | undefined>(undefined);

    // whether to continue the tour when the user comes back if the tour was cancelled before
    // by default it is false
    const continueOnCancelled = ref<boolean>(false);

    // Set this variable if you don't want to trigger the tour initially even when the walkthroughId is set
    // Then use the repeatWalkthroughFn to trigger the tour
    const isInitialTourEnabled = ref<boolean>(true);

    watch([walkthroughId, events], ([newId]) => {
        if (isNullable(newId)) {
            walkthroughEvent.value = undefined;
            return;
        }

        if (!isInitialTourEnabled.value) {
            walkthroughEvent.value = undefined;
            return;
        }

        // the events are not loaded yet
        if (isNullable(events.value)) {
            return;
        }

        // return if the walkthrough is already completed
        if (completedEvents.value?.some((event) => event.name === newId)) {
            return;
        }
        // return if the walkthrough was cancelled and it should not be continued
        if (cancelledEvents.value?.some((event) => event.name === newId) && !continueOnCancelled.value) {
            return;
        }

        walkthroughFn();
    });

    const onWalkthroughComplete = () => {
        // if the walkthrough was not completed or cancelled, add the event
        if (
            ![...(completedEvents.value ?? []), ...(cancelledEvents.value ?? [])].some(
                (event) => event.name === walkthroughId.value,
            )
        ) {
            addWalkthroughEvent(walkthroughEvent.value?.key ?? '', 'completed');
        }

        resetWalkthroughVariables();

        walkthroughFinishLatestStatus.value = undefined;
        walkthroughFinishLatestStatus.value = 'completed';
    };

    const onWalkthroughCancel = () => {
        // if the walkthrough was not completed or cancelled, add the event
        if (
            ![...(completedEvents.value ?? []), ...(cancelledEvents.value ?? [])].some(
                (event) => event.name === walkthroughId.value,
            )
        ) {
            addWalkthroughEvent(walkthroughEvent.value?.key ?? '', 'cancelled');
        }
        resetWalkthroughVariables();

        walkthroughFinishLatestStatus.value = undefined;
        walkthroughFinishLatestStatus.value = 'cancelled';
    };

    // do the walkthrough
    const walkthroughFn = () => {
        if (isNullable(walkthroughId.value)) {
            return;
        }

        // re-assigning with spread operator to re-trigger the walkthrough
        walkthroughEvent.value = { ...walkthroughEvents[walkthroughId.value] };
    };

    const repeatWalkthroughFn = () => {
        walkthroughFn();
    };

    const resetWalkthroughVariables = () => {
        continueOnCancelled.value = false;
    };

    const addWalkthroughEvent = (name: string, status: 'completed' | 'cancelled') => {
        if (isNullable(walkthroughEvent.value) || isNullable(user.value) || isNullable(currentOrganization.value)) {
            return;
        }
        const event: EventRowInsert = {
            status,
            name,
            type: 'onboarding',
            userId: user.value.id,
            organizationId: currentOrganization.value.id,
            metadata: {},
        };

        addEvent(event);
    };

    return {
        walkthroughId,
        walkthroughEvent,
        isInitialTourEnabled,
        walkthroughFinishLatestStatus,
        onWalkthroughComplete,
        onWalkthroughCancel,
        repeatWalkthroughFn,
    };
});

export { provideWalkthrough };
export const useWalkthrough = () => useWalkthroughNullable()!;
